import React from "react";
import {
  arrayOf,
  bool,
  func,
  number,
  oneOfType,
  shape,
  string
} from "prop-types";

import { formatMoney } from "src/shared/utilities/formatMoney";

import ImageBox from "../ImageBox";
import LicenseInfo from "../LicenseInfo";
import ListingInfo from "../ListingInfo";
import SaveButtonHeart from "../SaveButtonHeart/SaveButtonHeart";
import Tag from "../Tag";
import * as S from "./styled";

const ListingCardVertical = ({
  area,
  bathrooms,
  bedrooms,
  className,
  contacts,
  imgSrc,
  isFeatured,
  isNewDevelopment,
  isNoFee,
  isSaved,
  isVerified,
  netEffectiveRent,
  onCardClick,
  onSaveClick,
  openHouse,
  price,
  priceHistory,
  sqft,
  title,
  type,
  url
}) => {
  const renderPriceHistory = priceHistoryArr => {
    const isIncrease = priceHistoryArr[0].price > priceHistoryArr[1].price;
    const abs = Math.abs(priceHistoryArr[0].price - priceHistoryArr[1].price);
    if (isIncrease) {
      return <Tag text={abs.toString()} type="priceIncrease" />;
    }
    return <Tag text={abs.toString()} type="priceDecrease" />;
  };

  return (
    <S.ListingCardContainer className={className}>
      <S.LinkToProperty href={url} onClick={onCardClick} target="_blank">
        {title}
      </S.LinkToProperty>
      <ImageBox
        imgSrc={imgSrc}
        isFeatured={isFeatured}
        isVerified={isVerified}
        openHouse={openHouse}
      />
      <S.SaveButtonWrapper>
        <SaveButtonHeart isSaved={isSaved} onClickSave={() => onSaveClick()} />
      </S.SaveButtonWrapper>
      <S.InfoBox>
        <S.CardInfo>
          {isNewDevelopment && (
            <S.AreaInfo isNewDevelopment={isNewDevelopment}>
              New development
            </S.AreaInfo>
          )}
          <S.AreaInfo>{area}</S.AreaInfo>
          <S.Title>{title}</S.Title>
          <S.Price>
            {formatMoney(price)}
            {priceHistory.length > 1 && renderPriceHistory(priceHistory)}
            {isNoFee && <Tag text="no fee" type="noFee" />}
          </S.Price>
          {type === "Rental" && !!netEffectiveRent && (
            <S.PriceDetailsContainer>
              <S.NetEffectiveRent>
                <b>{netEffectiveRent.rent}</b> Net Effective Rent
              </S.NetEffectiveRent>
              {netEffectiveRent.concessionsMonths} &middot;{" "}
              {netEffectiveRent.concessionsLeaseTerm}
            </S.PriceDetailsContainer>
          )}
        </S.CardInfo>
        <S.BottomBox>
          <ListingInfo bath={bathrooms} beds={bedrooms} squareFootage={sqft} />
        </S.BottomBox>
      </S.InfoBox>
      {contacts.length > 0 && <LicenseInfo contacts={contacts} />}
    </S.ListingCardContainer>
  );
};

ListingCardVertical.defaultProps = {
  className: "",
  isNewDevelopment: false,
  isFeatured: false,
  isNoFee: false,
  isVerified: false,
  openHouse: null,
  netEffectiveRent: null,
  onCardClick: () => {},
  priceHistory: [],
  sqft: null,
  type: "Sale"
};

ListingCardVertical.propTypes = {
  area: string.isRequired,
  bathrooms: oneOfType([number, string]).isRequired,
  bedrooms: oneOfType([number, string]).isRequired,
  className: string,
  contacts: arrayOf(
    shape({
      id: string,
      business_url: string,
      business_name: string,
      full_business_address: string
    })
  ).isRequired,
  imgSrc: string.isRequired,
  isNoFee: bool,
  isNewDevelopment: bool,
  isFeatured: bool,
  isVerified: bool,
  onCardClick: func,
  openHouse: string,
  netEffectiveRent: shape({
    concessionsMonths: string,
    concessionsLeaseTerm: string,
    rent: string
  }),
  isSaved: bool.isRequired,
  url: string.isRequired,
  onSaveClick: func.isRequired,
  price: number.isRequired,
  priceHistory: arrayOf(shape({ price: number })),
  sqft: string,
  type: string,
  title: string.isRequired
};

export default ListingCardVertical;
